import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { gestorVisivel } from '../lib/filtroGestor'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts'

const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez']
const ANO_ATUAL = new Date().getFullYear()
const HOJE = new Date().toISOString().slice(0, 10)

const CORES_STATUS = { 'Aberto': '#EF4444', 'Em andamento': '#EF9F27', 'Concluído': '#22C55E' }
const CORES_CONCLUSAO = { 'Efetivo': '#22C55E', 'Não Efetivo': '#EF4444', 'Sem conclusão': '#9CA3AF' }

export default function AnalyticsFcas() {
  const { isAdmin, isGestor, profile, isSuperAdmin, selectedCompany, filtroGestor, setFiltroGestor } = useAuth()
  const navigate = useNavigate()
  const [fcas, setFcas] = useState([])
  const [loading, setLoading] = useState(true)
  const [ano, setAno] = useState(ANO_ATUAL)
  const [gestores, setGestores] = useState([])

  // Filtro de gestor compartilhado com as outras telas (ver AuthContext).
  // Quem nao enxerga o seletor tambem nao herda o recorte.
  const podeFiltrarGestor = isAdmin || isSuperAdmin || isGestor
  const gestorSel = podeFiltrarGestor ? gestorVisivel(filtroGestor, gestores) : ''

  useEffect(() => {
    if (isAdmin || isGestor || isSuperAdmin) {
      let q = supabase.from('profiles').select('id, nome').order('nome')
      if (isSuperAdmin && selectedCompany) q = q.eq('company_id', selectedCompany.id)
      q.then(({ data }) => {
        const lista = data || []
        setGestores(isGestor ? lista.filter(p => p.id !== profile?.id) : lista)
      })
    }
  }, [isAdmin, isGestor, isSuperAdmin, selectedCompany])

  useEffect(() => { fetchData() }, [ano, gestorSel, selectedCompany])

  async function fetchData() {
    setLoading(true)
    let q = supabase.from('fcas')
      .select('*, indicadores(id, nome, gestor_id, profiles(id, nome))')
      .eq('ano', ano)
      .order('mes')
    if (isSuperAdmin && selectedCompany) q = q.eq('company_id', selectedCompany.id)

    const { data } = await q
    const lista = (data || []).filter(f =>
      !gestorSel || f.indicadores?.profiles?.id === gestorSel
    )
    setFcas(lista)
    setLoading(false)
  }

  // --- Métricas gerais ---
  const total = fcas.length
  const comPlano = fcas.filter(f => f.acao?.trim()).length
  const abertos = fcas.filter(f => f.status === 'Aberto').length
  const emAndamento = fcas.filter(f => f.status === 'Em andamento').length
  const concluidos = fcas.filter(f => f.status === 'Concluído').length
  const efetivos = fcas.filter(f => f.conclusao === 'Efetivo').length
  const naoEfetivos = fcas.filter(f => f.conclusao === 'Não Efetivo').length
  const semConclusao = fcas.filter(f => f.status === 'Concluído' && !f.conclusao).length
  const atrasados = fcas.filter(f => f.status !== 'Concluído' && f.quando && f.quando < HOJE).length
  const taxaEfetividade = (efetivos + naoEfetivos) > 0
    ? Math.round((efetivos / (efetivos + naoEfetivos)) * 100)
    : null

  // --- Distribuição por status (donut) ---
  const distStatus = [
    { name: 'Aberto', value: abertos },
    { name: 'Em andamento', value: emAndamento },
    { name: 'Concluído', value: concluidos },
  ].filter(d => d.value > 0)

  // --- Distribuição de efetividade (donut) ---
  const distConclusao = [
    { name: 'Efetivo', value: efetivos },
    { name: 'Não Efetivo', value: naoEfetivos },
    { name: 'Sem conclusão', value: semConclusao },
  ].filter(d => d.value > 0)

  // --- Evolução mensal ---
  const evolucao = MESES.map((m, i) => {
    const doMes = fcas.filter(f => f.mes === i + 1)
    return {
      mes: m,
      Abertos: doMes.filter(f => f.status === 'Aberto' || f.status === 'Em andamento').length,
      Concluídos: doMes.filter(f => f.status === 'Concluído').length,
    }
  })

  // --- Top indicadores com mais FCAs ---
  const porIndicador = Object.values(
    fcas.reduce((acc, f) => {
      const id = f.indicadores?.id
      if (!id) return acc
      if (!acc[id]) acc[id] = { nome: f.indicadores.nome, total: 0, efetivos: 0, naoEfetivos: 0, abertos: 0, concluidos: 0 }
      acc[id].total++
      if (f.conclusao === 'Efetivo') acc[id].efetivos++
      if (f.conclusao === 'Não Efetivo') acc[id].naoEfetivos++
      if (f.status !== 'Concluído') acc[id].abertos++
      if (f.status === 'Concluído') acc[id].concluidos++
      return acc
    }, {})
  ).sort((a, b) => b.total - a.total)

  const anos = [ANO_ATUAL - 2, ANO_ATUAL - 1, ANO_ATUAL]

  const cards = [
    { label: 'Total de FCAs', val: total, color: 'var(--text)' },
    { label: 'Com plano de ação', val: comPlano, color: 'var(--blue)' },
    { label: 'Concluídos', val: concluidos, color: 'var(--green)' },
    { label: 'Efetivos', val: efetivos, color: 'var(--green)' },
    { label: 'Não Efetivos', val: naoEfetivos, color: naoEfetivos > 0 ? 'var(--red)' : 'var(--text)' },
    { label: 'Atrasados', val: atrasados, color: atrasados > 0 ? 'var(--red)' : 'var(--text)' },
    { label: 'Taxa de Efetividade', val: taxaEfetividade !== null ? `${taxaEfetividade}%` : '—', color: taxaEfetividade === null ? 'var(--text2)' : taxaEfetividade >= 70 ? 'var(--green)' : taxaEfetividade >= 40 ? 'var(--amber)' : 'var(--red)' },
  ]

  return (
    <div>
      <div className="page-title">
        <div>
          <h2>Analytics — FCAs</h2>
          <p style={{ color: 'var(--text2)', fontSize: 13, marginTop: 2 }}>
            Visão geral de implementação e efetividade das ações corretivas
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {podeFiltrarGestor && gestores.length > 0 && (
            <select value={gestorSel} onChange={e => setFiltroGestor(e.target.value)} style={{ width: 'auto', minWidth: 160 }}>
              <option value="">{isGestor ? 'Todos os coordenadores' : 'Todos os gestores'}</option>
              {gestores.map(g => <option key={g.id} value={g.id}>{g.nome}</option>)}
            </select>
          )}
          <select value={ano} onChange={e => setAno(Number(e.target.value))} style={{ width: 'auto' }}>
            {anos.map(a => <option key={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {/* Cards de resumo */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 10, marginBottom: 20 }}>
        {cards.map(c => (
          <div key={c.label} style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '14px 16px', boxShadow: 'var(--shadow)'
          }}>
            <div style={{ fontSize: 11, color: 'var(--text2)', marginBottom: 4, lineHeight: 1.3 }}>{c.label}</div>
            <div style={{ fontSize: 26, fontWeight: 700, color: loading ? 'var(--text3)' : c.color }}>
              {loading ? '—' : c.val}
            </div>
          </div>
        ))}
      </div>

      {/* Gráficos donuts */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Distribuição por Status</h3>
          {loading || distStatus.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px 0' }}>Sem dados</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={distStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {distStatus.map(d => <Cell key={d.name} fill={CORES_STATUS[d.name]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [v, n]} contentStyle={{ border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }} />
                <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 13 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card">
          <h3 style={{ marginBottom: 16 }}>Efetividade das Ações Concluídas</h3>
          {loading || distConclusao.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px 0' }}>Sem FCAs concluídos</div>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={distConclusao} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3}>
                  {distConclusao.map(d => <Cell key={d.name} fill={CORES_CONCLUSAO[d.name]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [v, n]} contentStyle={{ border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }} />
                <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 13 }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      {/* Evolução mensal */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginBottom: 16 }}>Evolução Mensal — {ano}</h3>
        {loading ? (
          <div className="empty-state">Carregando...</div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={evolucao} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e0d8" />
              <XAxis dataKey="mes" tick={{ fontSize: 12, fill: '#6b6960' }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#6b6960' }} />
              <Tooltip contentStyle={{ border: '1px solid var(--border)', borderRadius: 8, fontSize: 13 }} />
              <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
              <Bar dataKey="Abertos" fill="#EF9F27" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Concluídos" fill="#22C55E" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Tabela por indicador */}
      <div className="card" style={{ padding: 0 }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>FCAs por Indicador — {ano}</h3>
          <button className="btn btn-sm" onClick={() => navigate('/fcas')}>Ver FCAs →</button>
        </div>
        {loading ? (
          <div className="empty-state">Carregando...</div>
        ) : porIndicador.length === 0 ? (
          <div className="empty-state">Nenhum FCA no período.</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Indicador</th>
                  <th style={{ textAlign: 'center' }}>Total</th>
                  <th style={{ textAlign: 'center' }}>Em Aberto</th>
                  <th style={{ textAlign: 'center' }}>Concluídos</th>
                  <th style={{ textAlign: 'center' }}>Efetivos</th>
                  <th style={{ textAlign: 'center' }}>Não Efetivos</th>
                  <th style={{ textAlign: 'center' }}>Efetividade</th>
                </tr>
              </thead>
              <tbody>
                {porIndicador.map(ind => {
                  const tx = (ind.efetivos + ind.naoEfetivos) > 0
                    ? Math.round((ind.efetivos / (ind.efetivos + ind.naoEfetivos)) * 100)
                    : null
                  return (
                    <tr key={ind.nome}>
                      <td style={{ fontWeight: 500 }}>{ind.nome}</td>
                      <td style={{ textAlign: 'center', fontFamily: 'var(--mono)' }}>{ind.total}</td>
                      <td style={{ textAlign: 'center' }}>
                        {ind.abertos > 0 ? <span className="badge badge-red">{ind.abertos}</span> : <span style={{ color: 'var(--text3)' }}>—</span>}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {ind.concluidos > 0 ? <span className="badge badge-green">{ind.concluidos}</span> : <span style={{ color: 'var(--text3)' }}>—</span>}
                      </td>
                      <td style={{ textAlign: 'center', color: 'var(--green)', fontFamily: 'var(--mono)', fontWeight: ind.efetivos > 0 ? 600 : undefined }}>
                        {ind.efetivos > 0 ? ind.efetivos : '—'}
                      </td>
                      <td style={{ textAlign: 'center', color: ind.naoEfetivos > 0 ? 'var(--red)' : undefined, fontFamily: 'var(--mono)', fontWeight: ind.naoEfetivos > 0 ? 600 : undefined }}>
                        {ind.naoEfetivos > 0 ? ind.naoEfetivos : '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {tx !== null
                          ? <span className={`badge ${tx >= 70 ? 'badge-green' : tx >= 40 ? 'badge-amber' : 'badge-red'}`}>{tx}%</span>
                          : <span style={{ color: 'var(--text3)', fontSize: 13 }}>—</span>}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
